import { useNavigate, useLocation } from "react-router-dom"
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet"
import {
    Users,
    Building2,
    CreditCard,
    Bot,
    Settings,
    HelpCircle,
    ChevronRight,
    Sparkles,
    FileSignature,
    FileCheck,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { useAuth } from "@/components/auth-context"
import { useTranslation } from "react-i18next"
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher"

interface DrawerItem {
    icon: React.ElementType
    labelKey: string
    href: string
    pro?: boolean
    badge?: string
    descriptionKey?: string
}

const DRAWER_ITEMS: DrawerItem[] = [
    { icon: Building2, labelKey: "sidebar.clients", href: "/clients", descriptionKey: "drawer.clientsDesc" },
    { icon: FileSignature, labelKey: "sidebar.quotes", href: "/quotes", pro: true, descriptionKey: "drawer.quotesDesc" },
    { icon: Users, labelKey: "sidebar.team", href: "/team", badge: "Teams", descriptionKey: "drawer.teamDesc" },
    { icon: FileCheck, labelKey: "sidebar.approvals", href: "/approvals", badge: "Teams", descriptionKey: "drawer.approvalsDesc" },
    { icon: Bot, labelKey: "sidebar.aiAssistant", href: "/ai-assistant", pro: true, descriptionKey: "drawer.aiDesc" },
    { icon: CreditCard, labelKey: "sidebar.billing", href: "/billing", descriptionKey: "drawer.billingDesc" },
    { icon: Settings, labelKey: "drawer.settings", href: "/profile", descriptionKey: "drawer.settingsDesc" },
    { icon: HelpCircle, labelKey: "drawer.help", href: "/help", descriptionKey: "drawer.helpDesc" },
]

interface MobileDrawerProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function MobileDrawer({ open, onOpenChange }: MobileDrawerProps) {
    const navigate = useNavigate()
    const location = useLocation()
    const { organization, userRole } = useAuth()
    const isAdminOrOwner = userRole === 'admin' || userRole === 'owner'
    const { t } = useTranslation()

    const handleNavigate = (href: string) => {
        navigate(href)
        onOpenChange(false)
    }

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="bottom"
                className="h-auto max-h-[85vh] rounded-t-[24px] px-0 pb-0 flex flex-col"
            >
                <SheetHeader className="px-6 py-4 border-b border-border/50 shrink-0">
                    <SheetTitle className="text-left text-base font-bold flex items-center gap-2">
                        <span className="text-muted-foreground/70 text-sm font-medium">
                            {organization?.name || t('sidebar.mySpace')}
                        </span>
                    </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1 scrollbar-hide">
                    {DRAWER_ITEMS.filter(item => {
                        if (item.href === '/approvals' && !isAdminOrOwner) return false
                        return true
                    }).map((item) => {
                        const isActive = location.pathname === item.href

                        return (
                            <button
                                key={item.href}
                                onClick={() => handleNavigate(item.href)}
                                className={cn(
                                    "w-full flex items-center gap-4 px-3 py-3.5 rounded-xl transition-all duration-200 text-left group",
                                    isActive
                                        ? "bg-primary/10 text-primary"
                                        : "text-foreground hover:bg-muted/60 active:bg-muted"
                                )}
                            >
                                {/* Icon */}
                                <div className={cn(
                                    "p-2 rounded-xl transition-colors shrink-0",
                                    isActive
                                        ? "bg-primary/15 text-primary"
                                        : "bg-muted/60 text-muted-foreground group-hover:text-foreground"
                                )}>
                                    <item.icon className="h-4 w-4" />
                                </div>

                                {/* Label + description */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold text-sm">{t(item.labelKey)}</span>
                                        {item.badge && (
                                            <Badge
                                                variant="outline"
                                                className="text-[9px] h-4 px-1.5 bg-primary/5 text-primary border-primary/20 font-bold"
                                            >
                                                {item.badge}
                                            </Badge>
                                        )}
                                        {item.pro && (
                                            <Badge
                                                variant="outline"
                                                className="text-[9px] h-4 px-1.5 bg-primary/10 text-primary border-primary/20 font-bold shadow-sm"
                                            >
                                                <Sparkles className="h-2.5 w-2.5 mr-0.5" />
                                                PRO
                                            </Badge>
                                        )}
                                    </div>
                                    {item.descriptionKey && (
                                        <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                                            {t(item.descriptionKey, { defaultValue: '' })}
                                        </p>
                                    )}
                                </div>

                                {/* Chevron */}
                                <ChevronRight className={cn(
                                    "h-4 w-4 shrink-0 transition-transform",
                                    isActive ? "text-primary" : "text-muted-foreground/50 group-hover:translate-x-0.5"
                                )} />
                            </button>
                        )
                    })}
                </div>

                {/* Language Switcher + Bottom safe area padding */}
                <div className="px-4 pt-4 pb-[calc(2.5rem+env(safe-area-inset-bottom,0px))] flex justify-center border-t border-border/50 bg-background/50 backdrop-blur-sm shrink-0">
                    <LanguageSwitcher />
                </div>
            </SheetContent>
        </Sheet>
    )
}
